// ---------------------------------------------------------------------------
// Apple In-App Purchases — RevenueCat Capacitor bridge.
//
// The iOS shell (DigitalCemetery-iOS) registers the native 'Purchases'
// plugin; this site is loaded inside that shell, so the plugin is reached
// through window.Capacitor.Plugins — no npm import in the web bundle.
// On the plain website every helper is a safe no-op.
//
// Product IDs must match App Store Connect exactly (they are permanent):
//   soulemap.subscription.monthly   $6.99/mo   auto-renewable
//   soulemap.subscription.yearly    $69.99/yr  auto-renewable
//   soulemap.glow.night             $4.99      consumable
//   soulemap.glow.week              $12.99     consumable
//   soulemap.forever.star           $149.99    non-consumable
// ---------------------------------------------------------------------------

import { isInNativeApp } from './platform';

export const IAP_PRODUCTS = {
  monthly: 'soulemap.subscription.monthly',
  yearly: 'soulemap.subscription.yearly',
  glowNight: 'soulemap.glow.night',
  glowWeek: 'soulemap.glow.week',
  foreverStar: 'soulemap.forever.star',
};

const plugin = () =>
  (typeof window !== 'undefined' &&
    window.Capacitor &&
    window.Capacitor.Plugins &&
    window.Capacitor.Plugins.Purchases) ||
  null;

// True when purchases must go through Apple (native shell with the plugin).
export const iapAvailable = () => isInNativeApp() && !!plugin();

let configured = false;

// Configure once per app launch; safe to call repeatedly.
export async function configureIap(uid) {
  const P = plugin();
  if (!P) return false;
  const apiKey = process.env.REACT_APP_REVENUECAT_APPLE_KEY;
  if (!apiKey) {
    console.warn('IAP: REACT_APP_REVENUECAT_APPLE_KEY is not set');
    return false;
  }
  try {
    if (!configured) {
      await P.configure({ apiKey, appUserID: uid || undefined });
      configured = true;
    } else if (uid) {
      await P.logIn({ appUserID: uid });
    }
    return true;
  } catch (e) {
    console.warn('IAP configure failed:', e);
    return false;
  }
}

// Tie purchases to the signed-in Firebase user (webhook uses this id).
export async function iapLogIn(uid) {
  const P = plugin();
  if (!P || !uid) return;
  try {
    if (!configured) { await configureIap(uid); return; }
    await P.logIn({ appUserID: uid });
  } catch (e) {
    console.warn('IAP logIn failed:', e);
  }
}

// Buy one product by key from IAP_PRODUCTS. Resolves with the plugin's
// purchase result ({ customerInfo, productIdentifier, transaction? }).
// Throws on failure; user cancellation throws with .userCancelled = true.
export async function purchaseIap(productKey) {
  const P = plugin();
  if (!P) throw new Error('In-app purchases are not available here.');
  const productIdentifier = IAP_PRODUCTS[productKey];
  if (!productIdentifier) throw new Error(`Unknown product: ${productKey}`);
  if (!configured) {
    const ok = await configureIap();
    if (!ok) throw new Error('Purchases are not ready. Please try again.');
  }

  // Fetch the store product, then purchase it (purchases-capacitor v9 API).
  let products = [];
  try {
    const res = await P.getProducts({ productIdentifiers: [productIdentifier] });
    products = (res && (res.products || res)) || [];
  } catch (e) {
    throw new Error(e?.message || 'Could not reach the App Store. Please try again.');
  }

  if (!Array.isArray(products) || products.length === 0) {
    // StoreKit returns nothing while App Store Connect is still processing
    // the products or the Paid Applications agreement is not yet active.
    throw new Error(
      'The App Store has not made this purchase available yet. ' +
      'This usually resolves once Apple finishes processing — please try again later.'
    );
  }

  try {
    return await P.purchaseStoreProduct({ product: products[0] });
  } catch (e) {
    if (e && (e.userCancelled || /cancel/i.test(String(e.message || '')))) {
      const err = new Error('Purchase cancelled.');
      err.userCancelled = true;
      throw err;
    }
    throw new Error(e?.message || 'Purchase failed. Please try again.');
  }
}

// Apple requires a visible Restore Purchases control.
export async function restoreIapPurchases() {
  const P = plugin();
  if (!P) throw new Error('In-app purchases are not available here.');
  if (!configured) await configureIap();
  return P.restorePurchases();
}

// Does this device's Apple account hold the 'premium' entitlement?
export async function iapHasPremium() {
  const P = plugin();
  if (!P) return false;
  try {
    if (!configured) await configureIap();
    const res = await P.getCustomerInfo();
    const info = res && (res.customerInfo || res);
    return !!(info && info.entitlements && info.entitlements.active && info.entitlements.active.premium);
  } catch (e) {
    return false;
  }
}
